import { MenuItem, Icon } from "@blueprintjs/core"
import { cloneDeep, set } from "lodash-es"
import dayjs from "dayjs"
import React, { useEffect, useMemo, useState } from "react"
import CustomDialog from "components/Atoms/CustomDialog"
import CustomFormGroup from "components/Atoms/CustomFormGroup"
import StaticSelector from "components/Atoms/StaticSelector"
import StringInput from "components/Atoms/StringInput"
import FormController from "components/Molecules/FormController"
import CustomTimePicker from "components/Templates/form/CustomTimePicker"
import useAuth, { useCurrentDepartment } from "hooks/useAuth"
import { useCurrentDc } from "store/auth.redux"
import { useSelectedIds, RESET_SELECTED } from "store/selector.redux"
import ButtonMutation from "components/Molecules/ButtonMutation"
import { ADD_UPDATE_CONSOLIDATED_ORDER } from "graphql/order/order.mutations"
import { getErrors } from "hooks/useErrors"
import { consolidatedOrder } from "models/order.model"
import { ErrorToast, ApolloErrorToast, SuccessToast } from "utils/AppToaster"
import { useDispatch } from "react-redux"
const ConsolidateOrdersDialog = ({
  onClose,
  isOpen = false,
  ...otherProps
}) => {
  const [dc] = useCurrentDc()
  const dispatch = useDispatch()
  const deptId = useCurrentDepartment()
  const selectedOrders = useSelectedIds("orders")
  const [state, setState] = useState<any>({
    date: new Date().toISOString(),
  })

  function handleChange(path, value) {
    const stateCopy = cloneDeep(state)
    if (path === "orderingDcId") {
      set(stateCopy, "userId", null)
      if (value.id === state.targetDcId?.id) {
        set(stateCopy, "targetDcId", null)
      }
    }
    set(stateCopy, path, value)
    setState(stateCopy)
  }

  useEffect(() => {
    if (dc?.id) {
      setState(prev => ({
        ...prev,
        orderingDcId: dc,
        targetDcId: null,
      }))
    }
  }, [dc, setState])

  const parsedVariables = useMemo(() => {
    return {
      orderingDcId: state?.orderingDcId?.id,
      targetDcId: state?.targetDcId?.id,
      placedInId: deptId,
      userId: state?.userId?.id,
      date: dayjs(state?.date).format("YYYY-MM-DD"),
      orderFilter: undefined,
      orderIds: selectedOrders,
      remark: state?.remark ?? "",
    }
  }, [state, selectedOrders, deptId])
  const { distributionCentres } = useAuth()

  const users = state.orderingDcId?.pointOfContacts

  return (
    <CustomDialog
      isOpen={isOpen}
      title={
        <span className="text-muted">
          Consolidate <span className="vt-dark">{selectedOrders?.length}</span>{" "}
          Orders
        </span>
      }
      footerRightButton={
        <ButtonMutation
          icon="confirm"
          text="Consolidate Orders"
          mutation={ADD_UPDATE_CONSOLIDATED_ORDER}
          variables={parsedVariables}
          onError={err => {
            ApolloErrorToast(err)
          }}
          onCompleted={({ addUpdateConsolidatedOrder: aco }) => {
            const order = aco?.order
            SuccessToast(
              `Consolidated Order ${order?.trackingCode} created successfully from ${selectedOrders?.length} orders`
            )
            dispatch({
              type: RESET_SELECTED,
              path: "orders",
            })
            onClose()
          }}
          validate={state => {
            const errors = getErrors({ state, schema: consolidatedOrder })
            if (errors?.latest) {
              return ErrorToast(errors?.latest)
            }
            return true
          }}
        />
      }
      width="50%"
      onClose={onClose}
      {...otherProps}
    >
      <FormController source={state} onChange={handleChange}>
        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-3">
            <StaticSelector
              path="orderingDcId"
              label="Source DC"
              absolute
              items={distributionCentres}
            />
          </div>
          <div className="col-span-1 d-center">
            <Icon icon="arrow-right" />
          </div>
          <div className="col-span-4">
            <StaticSelector
              path="targetDcId"
              label="Target DC"
              absolute
              items={distributionCentres}
              itemRenderer={(item, { handleClick, modifiers }) => {
                return (
                  <MenuItem
                    disabled={item.id === state?.orderingDcId?.id}
                    active={modifiers.active}
                    key={item.id}
                    onClick={handleClick}
                    text={item?.name}
                  />
                )
              }}
            />
          </div>
          <div className="col-span-4">
            <StaticSelector path="userId" absolute label="User" items={users} />
          </div>
        </div>
        <div className="grid grid-cols-12 gap-4 mt-3">
          <div className="col-span-8">
            <StringInput big={false} label="Remark" path="remark" />
          </div>
          <div className="col-span-4">
            <CustomFormGroup label="Date">
              <CustomTimePicker fill absolute path="date" />
            </CustomFormGroup>
          </div>
        </div>
      </FormController>
    </CustomDialog>
  )
}

export default ConsolidateOrdersDialog
